import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useListSettings } from "./useListSettings";
import { buildMetaDrivenFields } from "./buildMetaDrivenFields";
import type { ListSettingsConfig, ListSettingsFieldConfig } from "./listSettingsTypes";

type UseMetaDrivenListSettingsParams = {
  config: ListSettingsConfig | null | undefined;
  columnIds?: string[]; // For strict mode: list of column id/accessorKey values
};

/**
 * Hook for meta-driven list settings
 * Fetches DocType meta and builds available fields dynamically
 * 
 * @param params - Configuration and optional column IDs for strict mode
 * @returns List settings hook result with isLoading flag
 */
export function useMetaDrivenListSettings(params: UseMetaDrivenListSettingsParams) {
  const { config, columnIds } = params;
  const key = config?.key;
  const metaConfig = config?.meta;
  const staticFields = config?.staticFields ?? [];
  const staticModeFields = config?.fields ?? [];
  const staticModeDefaultVisibleOrder = config?.defaultVisibleOrder ?? [];

  const enabled = !!metaConfig;

  // Fetch meta and build fields
  // Use stable primitives in queryKey instead of full metaConfig object
  const { data: metaFields, isLoading, error } = useQuery({
    queryKey: [
      "metaDrivenFields",
      metaConfig?.doctype ?? null,
      columnIds,
      {
        includeFieldtypes: metaConfig?.includeFieldtypes ?? null,
        excludeFieldtypes: metaConfig?.excludeFieldtypes ?? null,
        excludeFieldnames: metaConfig?.excludeFieldnames ?? null,
        respectInListView: metaConfig?.respectInListView ?? true,
        strictMode: metaConfig?.strictMode ?? false,
        requiredFieldnames: metaConfig?.requiredFieldnames ?? null,
        defaultVisibleFieldnames: metaConfig?.defaultVisibleFieldnames ?? null,
      },
    ],
    enabled,
    queryFn: async () => {
      if (!metaConfig) {
        // Should never run when enabled=false, but keep it safe.
        return { availableFields: staticFields, defaultVisibleOrder: [] as string[] };
      }
      const { availableFields, defaultVisibleOrder } = await buildMetaDrivenFields(
        metaConfig,
        columnIds
      );
      
      // Merge with static fields
      const allFields = [...availableFields, ...staticFields];
      
      // Merge default visible order (static fields at end)
      // By default, staticFields are visible unless explicitly opted out via defaultVisible=false.
      const staticFieldnames = staticFields
        .filter((f) => f.defaultVisible !== false)
        .map((f) => f.fieldname);
      const mergedDefaultOrder = [
        ...defaultVisibleOrder,
        ...staticFieldnames.filter(f => !defaultVisibleOrder.includes(f))
      ];

      return {
        availableFields: allFields,
        defaultVisibleOrder: mergedDefaultOrder,
      };
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const resolvedAvailableFields: ListSettingsFieldConfig[] = useMemo(() => {
    if (enabled) {
      if (error) {
        // Fallback to static fields on error
        if (import.meta.env.DEV) {
          console.warn("[useMetaDrivenListSettings] Meta fetch failed, using static fields:", error);
        }
        return staticFields;
      }
      return metaFields?.availableFields ?? staticFields;
    }
    return staticModeFields;
  }, [enabled, metaFields?.availableFields, staticFields, staticModeFields, error]);

  const resolvedDefaultVisibleOrder = useMemo(() => {
    if (enabled) {
      return metaFields?.defaultVisibleOrder ?? [];
    }
    return staticModeDefaultVisibleOrder;
  }, [enabled, metaFields?.defaultVisibleOrder, staticModeDefaultVisibleOrder]);

  // Use the standard useListSettings hook with computed values
  const listSettings = useListSettings({
    key: key || "default",
    availableFields: resolvedAvailableFields,
    defaultVisibleOrder: resolvedDefaultVisibleOrder,
  });

  return {
    ...listSettings,
    isLoading: enabled ? isLoading : false,
  };
}

